import { useDeactivateDirectPaymentLinkMutation } from '@/services/accounts';
import { Box, Button, Modal, toast } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

interface DeactivatePaymentLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DeactivatePaymentLinkModal({ isOpen, onClose }: DeactivatePaymentLinkModalProps) {
  const { id: accountId = '' } = useParams<{ id: string }>();
  const [deactivateDirectPaymentLink, { isLoading }] = useDeactivateDirectPaymentLinkMutation();

  const handleDeactivate = useCallback(async () => {
    try {
      await deactivateDirectPaymentLink({ accountId }).unwrap();
      toast.success('Payment link deactivated');
      onClose();
    } catch {
      toast.error('Failed to deactivate payment link');
    }
  }, [deactivateDirectPaymentLink, accountId, onClose]);

  return (
    <Modal ariaLabelledBy="deactivate-payment-link-title" isOpen={isOpen} onDismiss={onClose} maxWidth="480px">
      <Modal.Header id="deactivate-payment-link-title" title="Deactivate payment link?" onDismiss={onClose} />
      <Modal.Body>
        <Box as="p" fontSize="sm">
          The customer will no longer be able to use this link to make a payment. This action cannot be undone.
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Box direction="row" justifyContent="flex-end" childGap="sm" width="100">
          <Button
            variant="secondary"
            tone="neutral"
            size="md"
            type="button"
            onClick={onClose}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            tone="danger"
            size="md"
            type="button"
            onClick={handleDeactivate}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            Deactivate
          </Button>
        </Box>
      </Modal.Footer>
    </Modal>
  );
}
